import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { resellingClientsPaginatedAction } from "../../../redux/actions";
import "../../TeamDetails/TeamDetails.scss";
import { convertToTitleCase } from "../../../utils/CovertTableHeaderToTitleCase";
import MaterialIcons from "../../../assests/MaterialIcons/MaterialIcons";
import { menuItemSX } from "../../Builder/BuilderConst";

const formatCentsToCurrency = (cents?: number) => {
  if (cents === undefined || cents === null) return "$0.00";
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getInitials = (name?: string) => {
  if (!name) return "C";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const typographyStyle = {
  fontFamily: "var(--font-family-manrope)",
  fontWeight: 400,
  fontSize: "16px",
};

interface ResellingClientsProps {
  startDate?: Date | null;
  endDate?: Date | null;
}

const ResellingClients: React.FC<ResellingClientsProps> = ({
  startDate,
  endDate,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | "";
  }>({
    key: "",
    direction: "",
  });

  const { clientsData, clientsPagination, clientsLoading } = useSelector(
    (state: RootState) => state.reselling,
  );

  // Fetch clients from backend with date_from and date_to parameters
  useEffect(() => {
    const payload: any = {
      page,
      limit: rowsPerPage,
      sort_by: sortConfig.key || undefined,
      sort_order: sortConfig.direction || undefined,
    };

    if (startDate) {
      payload.date_from = format(new Date(startDate), "yyyy-MM-dd");
    }
    if (endDate) {
      payload.date_to = format(new Date(endDate), "yyyy-MM-dd");
    }

    dispatch(resellingClientsPaginatedAction(payload));
  }, [dispatch, page, rowsPerPage, sortConfig, startDate, endDate]);

  // Handle Sort matching TeamDetails pattern
  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  const getSortArrow = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <MaterialIcons.ArrowDropUp
        className="arrow-up-animation"
        style={{ width: "20px" }}
      />
    ) : (
      <MaterialIcons.ArrowDropUp
        className="arrow-down-animation"
        style={{ width: "20px" }}
      />
    );
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event: any) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  // Helper for Subscription price pill badge
  const renderSubscriptionPriceBadge = (client: any) => {
    const isResold = client?.is_resold_subscription !== false;
    const costCents = isResold
      ? client?.subscription_cost_cents
      : client?.retail_price_cents;

    if (costCents === undefined || costCents === null || costCents <= 0) {
      return null;
    }

    const dollars = Math.round(costCents / 100);
    return (
      <span
        style={{
          backgroundColor: "#F3F4F6",
          color: "#4B5563",
          fontSize: "12px",
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: "6px",
          display: "inline-block",
          marginLeft: "8px",
          fontFamily: "var(--font-family-manrope)",
          lineHeight: 1.2,
        }}
      >
        ${dollars}/mo
      </span>
    );
  };

  // Status Badge Component
  const renderStatusBadge = (client: any) => {
    const isRetail = client?.is_resold_subscription === false;
    if (isRetail) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 6,
            background: "#E8F0FE",
            color: "#1A73E8",
            padding: "4px 10px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: "nowrap",
            width: "fit-content",
            fontFamily: "var(--font-family-manrope)",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#1A73E8",
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          Retail subscription
        </div>
      );
    }

    const status = client?.status;
    if (!status) return "—";

    const raw = status.replace(/_/g, " ");
    const label = raw.charAt(0).toUpperCase() + raw.slice(1);
    const s = status.toLowerCase();

    let bg = "#E7F5DB";
    let color = "#5F9B20";

    if (s === "past_due" || s === "pastdue") {
      bg = "#FFE6D4";
      color = "#FF6600";
    } else if (s === "pending") {
      bg = "#FEF3C7";
      color = "#D97706";
    } else if (
      s === "canceled" ||
      s === "cancelled" ||
      s === "inactive" ||
      s === "failed"
    ) {
      bg = "#FDE2DF";
      color = "#EA4136";
    } else if (s === "trialing" || s === "trial") {
      bg = "#E8F0FE";
      color = "#1A73E8";
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 6,
          background: bg,
          color: color,
          padding: "4px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 500,
          whiteSpace: "nowrap",
          width: "fit-content",
          fontFamily: "var(--font-family-manrope)",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {label}
      </div>
    );
  };

  // Helper for Credits bought column cell
  const renderCreditsBoughtCell = (client: any) => {
    const creditsResold =
      client.credits_resold ?? client.credits_purchased ?? 0;
    const spendCents = client.credit_spend_cents ?? 0;

    return (
      <div>
        <div
          style={{
            fontSize: "14px",
            fontFamily: "var(--font-family-manrope)",
            color: "#111827",
            fontWeight: 500,
          }}
        >
          {creditsResold.toLocaleString()}
        </div>
        <div
          style={{
            fontSize: "12px",
            fontFamily: "var(--font-family-manrope)",
            color: "#6b7280",
            marginTop: "2px",
          }}
        >
          {formatCentsToCurrency(spendCents)}
        </div>
      </div>
    );
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            ...typographyStyle,
            fontSize: "20px",
            fontFamily: "var(--font-family-manrope)",
            fontWeight: 600,
            color: "#111827",
          }}
        >
          Client Accounts
        </Typography>
      </Box>

      <Box sx={{ backgroundColor: "var(--background-color)", width: "100%" }}>
        <div className="team-details-table-wrapper">
          {/* Clients Table */}
          <div style={{ width: "100%", overflowX: "auto" }}>
            <table className="business-table">
              <thead>
                <tr>
                  <th
                    style={{ width: "260px" }}
                    className={`sortable-header ${sortConfig.key === "business_name" ? "active" : ""}`}
                    onClick={() => handleSort("business_name")}
                  >
                    {convertToTitleCase("client")}{" "}
                    {getSortArrow("business_name")}
                  </th>
                  <th
                    style={{ width: "160px" }}
                    className={`sortable-header ${sortConfig.key === "status" ? "active" : ""}`}
                    onClick={() => handleSort("status")}
                  >
                    {convertToTitleCase("status")} {getSortArrow("status")}
                  </th>
                  <th
                    style={{ width: "160px" }}
                    className={`sortable-header ${sortConfig.key === "credits_resold" ? "active" : ""}`}
                    onClick={() => handleSort("credits_resold")}
                  >
                    {convertToTitleCase("credits bought")}{" "}
                    {getSortArrow("credits_resold")}
                  </th>
                  <th
                    style={{ width: "160px" }}
                    className={`sortable-header ${sortConfig.key === "billed_cents" ? "active" : ""}`}
                    onClick={() => handleSort("billed_cents")}
                  >
                    {convertToTitleCase("billed")}{" "}
                    {getSortArrow("billed_cents")}
                  </th>
                  <th
                    style={{ width: "160px" }}
                    className={`sortable-header ${sortConfig.key === "profit_cents" ? "active" : ""}`}
                    onClick={() => handleSort("profit_cents")}
                  >
                    {convertToTitleCase("your profit")}{" "}
                    {getSortArrow("profit_cents")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {clientsLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx}>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <Skeleton variant="circular" width={36} height={36} />
                          <Box>
                            <Skeleton width={140} height={20} />
                            <Skeleton width={100} height={14} />
                          </Box>
                        </div>
                      </td>
                      <td>
                        <Skeleton
                          width={80}
                          height={24}
                          sx={{ borderRadius: 3 }}
                        />
                      </td>
                      <td>
                        <Skeleton width={80} height={20} />
                      </td>
                      <td>
                        <Skeleton width={60} height={20} />
                      </td>
                      <td>
                        <Skeleton width={60} height={20} />
                      </td>
                    </tr>
                  ))
                ) : clientsData && clientsData.length > 0 ? (
                  clientsData.map((client: any, idx: number) => (
                    <tr key={client.business_id || idx}>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: 13,
                              fontFamily: "var(--font-family-manrope)",
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(client.business_name)}
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: "14px",
                                fontFamily: "var(--font-family-manrope)",
                                color: "#111827",
                                lineHeight: 1.3,
                              }}
                            >
                              {client.business_name || "—"}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontFamily: "var(--font-family-manrope)",
                                display: "flex",
                                alignItems: "center",
                                marginTop: "2px",
                              }}
                            >
                              <span>{client.client_email || "—"}</span>
                              {renderSubscriptionPriceBadge(client)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{renderStatusBadge(client)}</td>
                      <td>{renderCreditsBoughtCell(client)}</td>
                      <td>
                        <div
                          style={{
                            fontSize: "14px",
                            fontFamily: "var(--font-family-manrope)",
                            color: "#111827",
                            fontWeight: 500,
                          }}
                        >
                          {formatCentsToCurrency(client.billed_cents ?? 0)}
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            fontSize: "14px",
                            fontFamily: "var(--font-family-manrope)",
                            color: "#16A34A",
                            fontWeight: 700,
                          }}
                        >
                          {formatCentsToCurrency(client.profit_cents ?? 0)}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: "center",
                        padding: "32px 16px",
                        color: "#6b7280",
                        fontSize: "14px",
                        fontFamily: "var(--font-family-manrope)",
                      }}
                    >
                      No client accounts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls matching TeamDetails */}
          <div className="pagination">
            {clientsPagination && clientsPagination.totalItems > 0 && (
              <>
                <span>
                  {(page - 1) * rowsPerPage + 1}–
                  {Math.min(page * rowsPerPage, clientsPagination.totalItems)}{" "}
                  of {clientsPagination.totalItems} results shown
                </span>

                <div className="page-controls">
                  <Pagination
                    count={clientsPagination.totalPages || 1}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />

                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="small"
                    sx={{
                      ml: 2,
                      overflow: "hidden",
                      "& .MuiInputBase-root": {
                        backgroundColor: "#e3f2fd !important",
                        borderRadius: "var(--small-border-radius)",
                        fontWeight: 400,
                        backgroundClip: "padding-box !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#e3f2fd !important",
                        borderRadius: "var(--small-border-radius)",
                        backgroundClip: "padding-box !important",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "var(--small-border-radius)",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 1,
                          boxShadow: "none",
                          border: "1px solid rgba(0,0,0,0.06)",
                          minWidth: 240,
                          borderRadius: "var(--small-border-radius)",
                          p: 0.5,
                        },
                      },
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {[10, 20].map((val: number) => (
                      <MenuItem sx={menuItemSX} key={val} value={val}>
                        {val} / page
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </>
            )}
          </div>
        </div>
      </Box>
    </>
  );
};

export default ResellingClients;
